import { createContext, PropsWithChildren, useContext, useMemo, useState } from "react";
import { useUser } from "./UserContext";

export type OnboardingContextValue = {
  onboardingDialog: {
    isOpen: boolean;
    open: () => void;
    dismiss: () => void;
  };
  welcomeDialog: {
    isOpen: boolean;
    dismiss: () => void;
  };
  onOnboardingCompleted: () => void;
};

export const OnboardingContext = createContext<OnboardingContextValue | undefined>(
  undefined
);
OnboardingContext.displayName = "OnboardingContext";

export function OnboardingContextProvider({ children }: PropsWithChildren) {
  const user = useUser(false);

  const [wasDismissed, setWasDismissed] = useState(false);
  const isOnboardingRequired = user?.isOnboardingRequired === true;
  const isOnboardingDialogOpen = isOnboardingRequired && !wasDismissed;

  const [isWelcomeDialogOpen, setIsWelcomeDialogOpen] = useState(false);

  const providerValue = useMemo(
    () => ({
      onboardingDialog: {
        isOpen: isOnboardingDialogOpen,
        open: () => setWasDismissed(false),
        dismiss: () => setWasDismissed(true),
      },
      welcomeDialog: {
        isOpen: isWelcomeDialogOpen,
        dismiss: () => setIsWelcomeDialogOpen(false),
      },
      onOnboardingCompleted: () => {
        if (user?.isOneOnOne) {
          setIsWelcomeDialogOpen(true);
        }
      },
    }),
    [user?.isOneOnOne, isOnboardingDialogOpen, isWelcomeDialogOpen]
  );

  return (
    <OnboardingContext.Provider value={providerValue}>
      {children}
    </OnboardingContext.Provider>
  );
}

export default function useOnboardingContext() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error("Must be used within an OnboardingContextProvider");
  }
  return context;
}
